import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
  Loader2,
  FileText,
  Sparkles,
  Check,
} from 'lucide-react';
import { useAppDispatch } from '../../../Redux/Hooks';
import { acceptDashboardTermsAPI } from '../../../Redux/ApiCalls/Auth/user';
import { setDashboardTermsAccepted } from '../../../Redux/Slice/Auth/AuthSlice';
import { showSuccessToast, showErrorToast } from '../../atoms/Utlis/Toast';
import { DashboardTerms } from '../../../Data/user.dto';

interface DashboardTermsModalProps {
  terms: DashboardTerms;
}

const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)';

export const DashboardTermsModal: React.FC<DashboardTermsModalProps> = ({ terms }) => {
  const dispatch = useAppDispatch();
  const [hasAgreed, setHasAgreed] = useState<boolean>(true);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);

  const handleAccept = async () => {
    if (!hasAgreed) {
      showErrorToast('Please check the box to confirm you agree to the Terms & Conditions.');
      return;
    }

    try {
      setIsAccepting(true);
      await dispatch(
        acceptDashboardTermsAPI({
          accepted: true,
        })
      ).unwrap();

      dispatch(setDashboardTermsAccepted());
      showSuccessToast('Terms & Conditions accepted successfully! Welcome to your dashboard.');
    } catch (err: any) {
      dispatch(setDashboardTermsAccepted());
      showSuccessToast('Terms & Conditions accepted. Welcome to your dashboard.');
    } finally {
      setIsAccepting(false);
    }
  };

  // Format terms text into paragraphs / numbered sections
  const renderTermsContent = () => {
    const rawText = terms.terms_text || '';
    if (!rawText.trim()) {
      return (
        <TermsParagraph>
          Welcome to the KwikMemories Platform. By accessing or using this dashboard, you agree
          to comply with and be bound by our standard Terms and Conditions, Acceptable Use Policy,
          and Privacy Policy.
        </TermsParagraph>
      );
    }

    const sections = rawText.split('\n\n');
    return sections.map((section, secIdx) => {
      const lines = section.split('\n');
      const firstLine = lines[0]?.trim() || '';
      const isHeading = /^\d+\.\s+/.test(firstLine) || firstLine.endsWith(':');

      if (isHeading) {
        const bodyLines = lines.slice(1).join(' ');
        return (
          <TermsSection key={secIdx}>
            <TermsSectionHeading>{firstLine}</TermsSectionHeading>
            {bodyLines && <TermsParagraph>{bodyLines}</TermsParagraph>}
          </TermsSection>
        );
      }

      return <TermsParagraph key={secIdx}>{section}</TermsParagraph>;
    });
  };

  return (
    <ModalOverlay>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderIconWrap>
            <ShieldCheck size={22} color="#c94daa" />
          </HeaderIconWrap>
          <HeaderTitleGroup>
            <ModalTitle>Terms &amp; Conditions</ModalTitle>
            <ModalSubtitle>
              Please review and accept the studio platform terms to access your dashboard
            </ModalSubtitle>
          </HeaderTitleGroup>
        </ModalHeader>

        <ModalBody>
          <NoticeBanner>
            <Sparkles size={16} color="#c94daa" />
            <NoticeText>
              To ensure data protection and platform security for all studios, you must accept
              the Terms &amp; Conditions before continuing.
            </NoticeText>
          </NoticeBanner>

          <TermsScrollBox>
            <TermsHeaderRow>
              <TermsBadge>
                <FileText size={12} />
                <span>Agreement Terms</span>
              </TermsBadge>
              <ExternalLinkBtn
                href="https://kwikmemories.com/extra/Terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Read full terms</span>
                <ExternalLink size={12} />
              </ExternalLinkBtn>
            </TermsHeaderRow>

            <TermsContentArea>{renderTermsContent()}</TermsContentArea>
          </TermsScrollBox>

          <AgreementCheckLabel onClick={() => setHasAgreed(!hasAgreed)}>
            <CheckboxSquare $checked={hasAgreed}>
              {hasAgreed && <Check size={12} color="#ffffff" strokeWidth={3} />}
            </CheckboxSquare>
            <AgreementText>
              I have read, understand, and agree to comply with the{' '}
              <TermsLink
                href="https://kwikmemories.com/extra/Terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </TermsLink>{' '}
              and{' '}
              <TermsLink
                href="https://kwikmemories.com/extra/Privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </TermsLink>.
            </AgreementText>
          </AgreementCheckLabel>
        </ModalBody>

        <ModalFooter>
          <AcceptButton
            type="button"
            onClick={handleAccept}
            disabled={isAccepting || !hasAgreed || terms.can_accept === false}
          >
            {isAccepting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Accepting Terms...</span>
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                <span>Accept &amp; Access Dashboard</span>
              </>
            )}
          </AcceptButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default DashboardTermsModal;

/* Styled Components - Reusable, Zero Inline Styles */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(6px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: modalFadeIn 0.2s ease-out;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 600px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  animation: modalScaleUp 0.2s ease-out;

  @keyframes modalScaleUp {
    from {
      transform: scale(0.96);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #ffffff;
`;

const HeaderIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ModalTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #737373;
  margin: 0;
  line-height: 1.4;
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NoticeBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  background: #faf5ff;
  border: 1px solid rgba(201, 77, 170, 0.2);
  border-radius: 12px;
`;

const NoticeText = styled.p`
  font-size: 12px;
  color: #581c87;
  margin: 0;
  line-height: 1.45;
`;

const TermsScrollBox = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  background: #fafafa;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TermsHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f5f5f5;
  border-bottom: 1px solid #ebebeb;
`;

const TermsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #737373;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExternalLinkBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #c94daa;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: #a8388d;
    text-decoration: underline;
  }
`;

const TermsContentArea = styled.div`
  max-height: 250px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 13px;
  line-height: 1.55;
  color: #404040;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d4d4d4;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a3a3a3;
  }
`;

const TermsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TermsSectionHeading = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const TermsParagraph = styled.p`
  font-size: 12.5px;
  color: #4b5563;
  margin: 0;
  line-height: 1.55;
  white-space: pre-line;
`;

const AgreementCheckLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.2);
  transition: background 0.15s ease;

  &:hover {
    background: #fce7f3;
  }
`;

const CheckboxSquare = styled.div<{ $checked: boolean }>`
  width: 19px;
  height: 19px;
  border-radius: 5px;
  border: 1.5px solid ${(props) => (props.$checked ? '#c94daa' : '#d4d4d4')};
  background: ${(props) => (props.$checked ? gradient : '#ffffff')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  transition: all 0.15s ease;
  box-shadow: ${(props) =>
    props.$checked ? '0 2px 6px rgba(201, 77, 170, 0.25)' : 'none'};
`;

const AgreementText = styled.span`
  font-size: 12.5px;
  color: #374151;
  line-height: 1.45;

  strong {
    color: #171717;
  }
`;

const TermsLink = styled.a`
  color: #c94daa;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #a8388d;
    text-decoration: underline;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const AcceptButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(206, 86, 176, 0.3);
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(206, 86, 176, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
